import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Label } from '@hqo/ui-atoms';
import { Controller, ControllerRenderProps, useWatch, type UseFormReturn } from 'react-hook-form';
import { useFormMethods } from '@hqo/multi-step-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useBuildingData } from 'store/building/hooks';
import { selectDefaultCheckInOptionByUuidSelector } from 'store/building-settings/selectors';
import { VisitTypeData } from 'store/building/types';
import { useCheckInOptionsData } from 'components/visit-form/steps/visitor-step/visitor-form/use-check-in-options-data.hook';
import { useUserCheckInOptions } from 'components/visit-form/steps/visitor-step/visitor-form/use-user-check-in-options.hook';
import type { FieldConfig } from '../../../../hooks/use-ui-metadata-field-config.hook';
import type { CreateVisitFormV2Values } from '../../../../types/form-data.types';
import { useValidationMessage } from '../../../../hooks/use-validation-message.hook';
import { resolveDefaultVisitType } from './resolve-default-visit-type';

interface CheckInTypeProps {
  config: FieldConfig;
}

export const CheckInType = ({ config }: CheckInTypeProps): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const { visitTypes } = useBuildingData();
  const { availableCheckInOptions, tenantOverride } = useCheckInOptionsData();
  const { userShouldStopAtDesk, userShouldBypassDesk } = useUserCheckInOptions();
  const form = useFormMethods() as unknown as UseFormReturn<CreateVisitFormV2Values>;
  const { control, setValue } = form;

  const params = useParams<{ ownerId: string }>();
  const defaultCheckInOptionSelector = useMemo(
    () => selectDefaultCheckInOptionByUuidSelector(params.ownerId || ''),
    [params.ownerId],
  );
  const defaultCheckInOption = useSelector(defaultCheckInOptionSelector);
  const effectiveDefault = tenantOverride?.defaultCheckInOption ?? defaultCheckInOption;

  const effectiveAvailable = tenantOverride?.availableCheckInOptions ?? availableCheckInOptions;
  const hasVisitTypes = visitTypes && visitTypes.length > 0;

  // Seed a valid `visitTypeId` when the user cannot pick one from a radio
  // (STOP_AT_DESK / BYPASS_DESK builds hide the picker), otherwise the
  // create request is submitted without `visit_type_id` and the persisted
  // visit becomes uneditable in the admin app.
  // Watched (not read via getValues) so the seed re-applies after the parent
  // form resets to building defaults, which wipes it back to '' — leaving a
  // required field that has no visible input and silently blocks submission.
  const visitTypeIdValue = useWatch({ control, name: 'visitTypeId' });
  useEffect(() => {
    if (!hasVisitTypes || visitTypeIdValue) {
      return;
    }

    const matchingType = resolveDefaultVisitType(visitTypes, effectiveDefault, availableCheckInOptions, tenantOverride);

    if (matchingType) {
      setValue('visitTypeId', matchingType.id.toString(), { shouldValidate: false });
    }
  }, [
    hasVisitTypes,
    visitTypes,
    effectiveDefault,
    availableCheckInOptions,
    tenantOverride,
    setValue,
    visitTypeIdValue,
  ]);

  const onChange = useCallback(
    (field: ControllerRenderProps<CreateVisitFormV2Values, 'visitTypeId'>, visitType: VisitTypeData) => () => {
      field.onChange(visitType.id.toString());
    },
    [],
  );

  if (config.hidden || !hasVisitTypes || !effectiveAvailable) {
    return null;
  }

  if (userShouldStopAtDesk(availableCheckInOptions, tenantOverride)) {
    return (
      <p className="text-sm italic text-gray-500 mb-6">{formatMessage({ id: 'fields.visit_type_id.stopAtDesk' })}</p>
    );
  }

  if (userShouldBypassDesk(availableCheckInOptions, tenantOverride)) {
    return (
      <p className="text-sm italic text-gray-500 mb-6">{formatMessage({ id: 'fields.visit_type_id.bypassDesk' })}</p>
    );
  }

  // Only show radio selector when BOTH options are available
  // (STOP_AT_DESK-only and BYPASS_DESK-only are handled above as static messages)

  return (
    <div className="space-y-2" data-cy="check-in-type">
      <Label className="text-sm font-medium text-foreground gap-1">
        {formatMessage({
          id: 'createVisitFormV2.visitStep.checkInType',
          defaultMessage: 'Check-in type',
        })}
        {config.required && <span className="text-destructive">*</span>}
      </Label>
      <Controller
        name="visitTypeId"
        control={control}
        // eslint-disable-next-line react/jsx-no-bind
        render={({ field, fieldState }) => (
          <>
            <div className="flex flex-col gap-2">
              {visitTypes?.map((visitType) => (
                <label
                  key={visitType.id}
                  htmlFor={visitType.id.toString()}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    id={visitType.id.toString()}
                    type="radio"
                    name="visitTypeId"
                    value={visitType.id.toString()}
                    checked={field.value === visitType.id.toString()}
                    onChange={onChange(field, visitType)}
                    className="h-4 w-4 text-primary border-gray-300"
                  />
                  <span className="text-sm">
                    {formatMessage({
                      id: `labels.visitTypes.${visitType.code.replace(/-/g, '_')}`,
                      defaultMessage: visitType.name,
                    })}
                  </span>
                </label>
              ))}
            </div>
            {fieldState.error && (
              <p className="text-sm text-destructive">{formatValidationMessage(fieldState.error.message)}</p>
            )}
          </>
        )}
      />
    </div>
  );
};
